// src/view/hud.ts — DOM HUD (DESIGN §14, ARCHITECTURE §10): kendi köklerini programatik kurar (index.html'e bağımlı değil; tek <style>).
// Baş üstü etiketler (ad + oyuncu oku, hasar yüzdesi renk kademesi beyaz→sarı→kırmızı, %120+ kırmızı halka, sersemlik halkası + "!" ≥ 55
// ve 100'de titreşim, guard halkası yalnız bloklarken (< 30 çatlak), tutulan/taşınanda mash ikonu, doğuş korumasında yanıp sönen ana hat);
// köşe kartları (portre / baş harf, ad, 3 can, yüzde; YEREL oyuncular kendi renginde vurgulu; elenen gri); merkez (geri sayım, RING OUT atıf 1.2 s, kazanan paneli
// + skor + Tekrar/Menü); ekran kenarı kırmızı düşüş flaşı; ?stats=1 kutusu. Etiket konumu `camera.project` ile karede bir.
// Aynı ekranda 1–4 yerel oyuncu (config/players): "me" işaretleri tek id yerine YEREL OYUNCU KÜMESİNDEN gelir — her oyuncunun
// etiketi/kartı kendi renginde (playerColor(index)) çerçevelenir, baş üstü oku o renktedir, "⟲ BAS!" çağrısı yalnız yerel
// oyunculara yazılır. Küme boşsa (duman testi / izleyici) hiçbiri işaretlenmez.
// Ekran-uzayı ayırma (orkestratör kararı 3): tüm etiketler projekte edildikten sonra ikinci geçişte çakışanlar bir etiket yüksekliği
// (LABEL_H 79 px ölçüldü: ad 14 + halka 44 + yüzde 19 — 22 px yetersizdi, adlar alttaki halkanın üstüne biniyordu) + LABEL_GAP kadar yukarı
// itilir (oyuncu etiketi sabit; sonra yüksek id hareket eder). Yukarıda YER KALMAYINCA (sütun ekranın üstünden taşacaksa) yukarı değil
// YATAY kaçış (±LABEL_SHIFT_X), o da olmazsa aşağı; itilen (kompakt) etiket başına kısa bağlantı çizgisi çizer.
// Falling/Respawning/Eliminated ve viewport dışı etiket gizli. DOM yazımları yalnız değer değişince
// (son yazılan değer önbellekleri); viewport boyutu ResizeObserver / resize(w,h) ile önbellekte — update'te DOM ölçümü yok.
import * as THREE from 'three';
import type { Fighter, SimEvent, SimWorld, Vec3 } from '../core/types.ts';
import { Ev, FState } from '../core/types.ts';
import { clamp01 } from '../core/math.ts';
import { MAX_FIGHTERS } from '../config/constants.ts';
import { T } from '../config/tuning.ts';
import type { CharacterDef } from '../config/characters.ts';
import { playerColor } from '../config/players.ts';
import { PORTRAIT_URLS, assetUrl } from './assets.ts';

/** DESIGN §14 eşikleri: yüzde renk kademesi 0/100/200; kırmızı halka ≥ 120; guard çatlak < 30; RING OUT yazısı 1.2 s (tuning'de yok → yerel). */
// edgeFlashSec 0: ring-out'ta ekran kenarındaki kırmızı flaş KALDIRILDI (kullanıcı isteği — "hepsini bir kaldıralım").
// 0 iken flaş hiç gösterilmez; düşüşü hâlâ "RING OUT — X → Y" paneli, kamera sarsıntısı ve düşüş sesi anlatıyor.
export const HUD = { pctYellow: 100, pctRed: 200, dangerPct: 120, guardCrack: 30, ringoutSec: 1.2, edgeFlashSec: 0 } as const;

/** Hasar yüzdesi rengi: 0 beyaz → 100 sarı → 200 kırmızı (üstü kırmızı). */
export function damageColor(dmg: number): string {
  if (dmg <= HUD.pctYellow) { const t = clamp01(dmg / HUD.pctYellow); return `rgb(255,255,${Math.round(255 - 180 * t)})`; }
  const t = clamp01((dmg - HUD.pctYellow) / (HUD.pctRed - HUD.pctYellow));
  return `rgb(255,${Math.round(230 - 190 * t)},${Math.round(75 - 55 * t)})`;
}
/** Sersemlik halkası doluluğu 0..1 ve "!" (bitirilebilir, COMBAT.heavyKnockdownStun 55). */
export const stunFraction = (stun: number): number => clamp01(stun / T.COMBAT.stunMax);
export const stunFinishable = (stun: number): boolean => stun >= T.COMBAT.heavyKnockdownStun;
export const guardCracked = (guard: number): boolean => guard < HUD.guardCrack;
/** "RING OUT — X → Y" / "X kendi düştü" (DESIGN §8 atıf; FellEvent.target puanı alan). */
export function attributionText(e: SimEvent, nameOf: (id: number) => string): string {
  return e.target >= 0 ? `RING OUT — ${nameOf(e.target)} → ${nameOf(e.actor)}` : `RING OUT — ${nameOf(e.actor)} kendi düştü`;
}
/** Düşüş yönü → ekran kenarı sınıfı (kamera yaw 0: +x sağ, +z alt (kameraya doğru), −z üst). */
export function edgeSide(dx: number, dz: number): 'left' | 'right' | 'top' | 'bottom' {
  return Math.abs(dx) >= Math.abs(dz) ? (dx >= 0 ? 'right' : 'left') : (dz >= 0 ? 'bottom' : 'top');
}

const CSS = `
#rhud{position:fixed;inset:0;pointer-events:none;font:14px system-ui,sans-serif;color:#e6ebf3;overflow:hidden;z-index:10}
#rhud *{box-sizing:border-box}
.rh-label{position:absolute;transform:translate(-50%,-100%);text-align:center;white-space:nowrap;will-change:transform}
.rh-label .n{font-size:12px;opacity:.9;text-shadow:0 1px 2px #000}
/* Yerel oyuncu: ad KENDİ renginde (--pc etiket kökünde) ve ince çerçeveli — dört kişilik ekranda "hangisi benim" tek bakışta.
   Dikey pay 0: çerçeve etiket yüksekliğini yalnız 2 px (kenarlık) artırır, LABEL_H ölçümü (79) geçerli kalır. */
.rh-label .n.me{color:var(--pc,#ffd43b);font-weight:700;border:1px solid var(--pc,#ffd43b);border-radius:6px;padding:0 5px;background:rgba(10,13,19,.45)}
.rh-label .n.me::before{content:"▼ ";color:var(--pc,#ffd43b)}
.rh-label .p{font-weight:700;font-size:16px;text-shadow:0 1px 3px #000}
.rh-label .ring{position:relative;width:44px;height:44px;margin:0 auto}
.rh-label svg{position:absolute;inset:0;width:44px;height:44px;transform:rotate(-90deg)}
.rh-label .stun{stroke:#ffb347;fill:none;stroke-width:3;stroke-dasharray:125.6;transition:stroke-dashoffset .05s linear}
.rh-label .guard{stroke:#6ec6ff;fill:none;stroke-width:3;stroke-dasharray:6 3;display:none}
.rh-label .guard.on{display:block}.rh-label .guard.crack{stroke:#ff6b6b;stroke-dasharray:3 5}
.rh-label .bang{position:absolute;inset:0;display:none;align-items:center;justify-content:center;font-weight:900;font-size:20px;color:#ff5252;text-shadow:0 0 6px #000}
.rh-label .bang.on{display:flex}
.rh-label.stunmax .stun{stroke:#ff3b3b;animation:rh-shake .12s infinite}
.rh-label.danger .ring::after{content:"";position:absolute;inset:-6px;border:2px solid #ff3b3b;border-radius:50%;opacity:.8}
.rh-label .mash{display:none;font-size:12px;color:var(--pc,#ffd43b);text-shadow:0 1px 2px #000}.rh-label .mash.on{display:block;animation:rh-pulse .25s infinite alternate}
.rh-label.prot .n{outline:1px solid rgba(255,255,255,.8);outline-offset:2px;animation:rh-blink .17s infinite alternate}
.rh-label.compact .ring{display:none}.rh-label.compact .p.b::after{content:" !";color:#ff5252}
.rh-label .lead{position:absolute;left:50%;top:100%;width:1px;height:0;transform-origin:top center;background:linear-gradient(rgba(255,255,255,.55),rgba(255,255,255,0))}
@keyframes rh-shake{0%{transform:rotate(-90deg) translate(0,0)}50%{transform:rotate(-90deg) translate(1px,-1px)}100%{transform:rotate(-90deg) translate(-1px,1px)}}
@keyframes rh-pulse{from{opacity:.5}to{opacity:1}}
@keyframes rh-blink{from{opacity:1}to{opacity:.4}}
.rh-cards{position:absolute;left:0;right:0;bottom:12px;display:flex;justify-content:center;gap:10px;padding:0 12px}
.rh-card{display:flex;align-items:center;gap:8px;background:rgba(10,13,19,.66);border:1px solid rgba(255,255,255,.12);border-radius:8px;padding:6px 10px;flex:0 1 170px;min-width:0;transition:opacity .2s}
.rh-card>div:last-child{min-width:0}.rh-card .nm{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;display:flex;align-items:baseline;gap:5px}
/* Ping rozeti: ad KISALIR (min-width:0 + ellipsis), rozet kısalmaz — uzun adlar gecikmeyi kırpmasın.
   Renk eşikleri T.NET.profiles[...].rttMaxMs değil, oynanabilirlik hissi: yeşil rahat, sarı fark edilir, kırmızı bozar. */
.rh-card .nm .who{overflow:hidden;text-overflow:ellipsis;min-width:0}
.rh-card .ping{flex:none;font-size:10px;font-variant-numeric:tabular-nums;opacity:.75;color:#8b93a7}
.rh-card .ping.mid{color:#ffd43b}.rh-card .ping.bad{color:#ff5252}
.rh-card .ping:empty{display:none}
/* Yerel oyuncu kartı: çerçeve o oyuncunun rengi (--pc kartın kendisinde). color-mix desteklenmezse yalnız hâle düşer, kenarlık kalır. */
.rh-card.me{border-color:var(--pc,#ffd43b);box-shadow:0 0 0 1px color-mix(in srgb, var(--pc,#ffd43b) 40%, transparent)}
/* Bot canları NÖTR gri (.rh-card .lives i); yalnız yerel oyuncularınki kendi renginde. Dört kişilik ekranda
   botun altın sarısı noktaları P1'inkilerle karışıyordu. */
.rh-card.me .lives i{background:var(--pc,#ffd43b)}
.rh-card.out{filter:grayscale(1);opacity:.45}
.rh-card .pic{width:40px;height:40px;border-radius:6px;background:#2a3140 center/cover no-repeat;display:flex;align-items:center;justify-content:center;font-weight:700}
.rh-card .nm{font-size:12px;opacity:.85}.rh-card .pct{font-weight:700;font-size:18px}
.rh-card .lives{display:flex;gap:3px;margin-top:2px}.rh-card .lives i{width:9px;height:9px;border-radius:50%;background:#8b93a7;display:block}
.rh-card .lives i.off{background:#3a4152}
.rh-center{position:absolute;left:0;right:0;top:18%;text-align:center}
.rh-count{font-size:96px;font-weight:900;text-shadow:0 3px 12px #000;display:none}
.rh-ringout{font-size:32px;font-weight:800;color:#ff5252;text-shadow:0 2px 8px #000;display:none;margin-top:12px}
.rh-panel{display:none;pointer-events:auto;margin:40px auto 0;background:rgba(10,13,19,.85);border:1px solid rgba(255,255,255,.15);border-radius:12px;padding:18px 24px;width:min(520px,90vw)}
.rh-panel h2{margin:0 0 10px;font-size:28px;color:#ffd43b}
.rh-panel table{width:100%;border-collapse:collapse;font-size:13px}.rh-panel td,.rh-panel th{padding:4px 6px;text-align:left;border-bottom:1px solid rgba(255,255,255,.08)}
.rh-panel .btns{display:flex;gap:10px;justify-content:center;margin-top:14px}
.rh-panel button{font:inherit;padding:8px 18px;border-radius:8px;border:1px solid rgba(255,255,255,.2);background:#243049;color:#e6ebf3;cursor:pointer}
.rh-panel button:hover{background:#2f3f60}
.rh-edge{position:absolute;inset:0;opacity:0;transition:opacity .25s}
.rh-edge.left{background:linear-gradient(90deg,rgba(255,40,40,.55),transparent 30%)}
.rh-edge.right{background:linear-gradient(270deg,rgba(255,40,40,.55),transparent 30%)}
.rh-edge.top{background:linear-gradient(180deg,rgba(255,40,40,.55),transparent 30%)}
.rh-edge.bottom{background:linear-gradient(0deg,rgba(255,40,40,.55),transparent 30%)}
.rh-stats{position:absolute;top:8px;left:8px;font:12px ui-monospace,monospace;background:rgba(10,13,19,.6);padding:6px 8px;border-radius:6px;white-space:pre;display:none}
`;

/** HUD'un yerel oyuncudan ihtiyaç duyduğu her şey: hangi dövüşçü, kaçıncı oyuncu (renk buradan: playerColor(index)).
 *  config/players `LocalPlayer` bu şekle yapısal olarak uyar → entegrasyon kendi listesini doğrudan geçebilir. */
export interface LocalPlayerRef { fighterId: number; index: number }

export interface HudDeps {
  camera: THREE.PerspectiveCamera;
  /** Aynı ekrandaki insan oyuncular (0–4). Boş/verilmemiş + playerId < 0 → izleyici/duman testi: hiçbir dövüşçü işaretlenmez. */
  localPlayers?: readonly LocalPlayerRef[];
  /** Tek oyunculu geriye dönük yol: localPlayers verilmezse `[{ fighterId: playerId, index: 0 }]` sayılır (< 0 → kimse). */
  playerId?: number;
  chars: readonly CharacterDef[];            // Fighter.charIdx → def
  /** Etiketin dünya noktası (fighterView.headWorld); false → gizli. */
  headWorld: (id: number, out: Vec3) => boolean;
  container?: HTMLElement;
  stats?: boolean;
  onRematch?: () => void;
  onMenu?: () => void;
  /** Kazanan panelinin düğmeleri: çevrimdışı 'local' (Tekrar + Menü), ağda 'net' (yalnız "Lobiye dön").
   *  Varsayılan 'local' kalmak zorunda — yoksa çevrimdışı maçta Tekrar kaybolur (DESIGN §13 madde 4). */
  winnerActions?: 'local' | 'net';
  /** Ağ maçında bir dövüşçünün ping'i (ms); bot/ölçülmemiş → −1. Verilmezse kartlarda ping yazmaz
   *  (çevrimdışı maçta gecikme diye bir şey yok, sıfır yazmak yanıltıcı olurdu). */
  pingOf?: (fighterId: number) => number;
}
export interface HudStats {
  msSim: number; msView: number; msRender: number; drawCalls: number; fps: number; tier: string;
  /** Bot think/frame ve presenter (olay → kamera/vfx/ses/hud) süreleri (game.ts ayrı ölçer; verilmezse satırda yok). */
  msBot?: number; msPresent?: number;
  /** Gölge geçişi / sahne geçişi çağrıları (stage.stats; verilmezse yalnız toplam yazılır — game.ts entegrasyonu). */
  drawCallsShadow?: number; drawCallsNoShadow?: number;
  /** Olay havuzu taşmasıyla düşen olaylar (kümülatif; game.ts stats.eventsDropped — verilmezse satırda yok). */
  eventsDropped?: number;
  /** Oyun zaman çarpanı (1 = normal). < 1 iken kutuda "AĞIR ÇEKİM" yazar — özelliğin çalıştığı gözle görülsün. */
  timeScale?: number;
}

interface Label { root: HTMLDivElement; name: HTMLSpanElement; pct: HTMLDivElement; stun: SVGCircleElement; guard: SVGCircleElement; bang: HTMLDivElement; mash: HTMLDivElement; lead: HTMLDivElement }
interface Card { root: HTMLDivElement; nm: HTMLElement; ping: HTMLSpanElement; pct: HTMLDivElement; lives: HTMLElement[] }

const _w = new THREE.Vector3();
const _v: Vec3 = { x: 0, y: 0, z: 0 };
const STUN_CIRC = 125.6;   // 2π·20 (r 20)
/** Ping rozeti yenileme aralığı (s) ve renk eşikleri (ms). Sunucu ölçümü 1 Hz; 0.25 s yeterince canlı. */
const PING_REFRESH_SEC = 0.25;
const PING_MID_MS = 90;
const PING_BAD_MS = 160;

/** Ekran-uzayı etiket ayırma (orkestratör kararı 3; tahsissiz — girdiye göre deterministik, modül scratch'i `_shift`; DOM'suz test edilir). Görünür etiketler y'ye göre sıralanır
 *  (yerinde ekleme sıralaması, `order` scratch), O(n²) çift kontrolü: |dx| < LABEL_W ∧ dikey kaplar (LABEL_GAP payıyla) kesişiyor =
 *  çakışma (etiket kabı [y − h, y] aralığını kaplar: ad + halka + yüzde birlikte taşınır). YEREL OYUNCU etiketleri sabit
 *  (`anchorMask` biti 1 = sabit; dövüşçü kimliği < MAX_FIGHTERS = 8 → tek sayı, tahsis yok), iki sabit çakışırsa / hiç sabit
 *  yoksa yüksek id hareket eder ve KOMPAKT olur (`compact[i] = 1`: halka gizli, h = LABEL_H_COMPACT). Hareket sırası:
 *    1. yukarı — alttakinin kabının LABEL_GAP üstüne (etiketin tepesi LABEL_TOP_MARGIN'in altına inmiyorsa);
 *    2. yer yoksa YATAY kaçış — bir kez ±LABEL_SHIFT_X (viewport dışına taşarsa öbür yön), sütun ekranın üstünden taşmasın diye;
 *    3. o da olmazsa aşağı — alttakinin altına (maxY = kart bölgesinin üstü aşılmadan).
 *  4–5 etiketlik kümede eski tekdüze yukarı itme 5 × 74 px'lik sütun yapıp ekranın üstünden kesiliyordu (3. tur bulgusu). */
const _shift = new Uint8Array(MAX_FIGHTERS);
export function separateLabels(sx: Float64Array, sy: Float64Array, vis: Uint8Array, order: Uint8Array, compact: Uint8Array,
  anchorMask: number, viewW: number, maxY: number): void {
  let n = 0;
  compact.fill(0); _shift.fill(0);
  for (let i = 0; i < vis.length; i++) {
    if (!vis[i]) continue;
    let k = n++;
    while (k > 0 && sy[order[k - 1]!]! > sy[i]!) { order[k] = order[k - 1]!; k--; }
    order[k] = i;
  }
  const half = LABEL_W / 2;
  for (let round = 0; round < 2 * n; round++) {   // 2n tur: yatay kaçış + aşağı itme sıralamayı bozabildiği için tek geçiş yetmiyor
    let moved = false;
    for (let i = 0; i < n; i++) {
      const a = order[i]!;
      for (let j = i + 1; j < n; j++) {
        const b = order[j]!;
        if (Math.abs(sx[a]! - sx[b]!) >= LABEL_W) continue;
        // kaplar [y − h, y] + LABEL_GAP payı: kesişme ⇔ ya > top(b) ∧ yb > top(a)
        const ha = compact[a] ? LABEL_H_COMPACT : LABEL_H, hb = compact[b] ? LABEL_H_COMPACT : LABEL_H;
        if (sy[a]! <= sy[b]! - hb - LABEL_GAP || sy[b]! <= sy[a]! - ha - LABEL_GAP) continue;
        // Sabit (yerel oyuncu) etiketi yerinde kalır; ikisi de sabit ya da ikisi de değilse yüksek id hareket eder
        const aFix = (anchorMask >>> a) & 1, bFix = (anchorMask >>> b) & 1;
        const mover = aFix && !bFix ? b : bFix && !aFix ? a : a > b ? a : b;
        const other = mover === a ? b : a;
        const st = _shift[mover]!;                               // bit 0: yatay kaçış kullanıldı, bit 1: aşağı itildi (artık yukarı çıkmaz)
        const up = sy[other]! - (mover === a ? hb : ha) - LABEL_GAP;
        if (!(st & 2) && up - LABEL_H_COMPACT >= LABEL_TOP_MARGIN) {   // 1. yukarı (kompaktın tepesi ekranda kalıyor)
          if (sy[mover]! > up) { sy[mover] = up; compact[mover] = 1; moved = true; }
          continue;
        }
        if (!(st & 1)) {                                         // 2. yatay kaçış (LABEL_W eşiğini AŞAN tek adım → çakışma çözülür)
          // Aynı x'teki kümede yön id paritesiyle bölünür (hepsi aynı tarafa gidip yeni sütun yapmasın)
          const dir = sx[mover]! !== sx[other]! ? (sx[mover]! > sx[other]! ? 1 : -1) : (mover & 1 ? 1 : -1);
          let nx = sx[mover]! + dir * LABEL_SHIFT_X;
          if (nx < half || nx > viewW - half) nx = sx[mover]! - dir * LABEL_SHIFT_X;
          if (nx >= half && nx <= viewW - half) {
            sx[mover] = nx; _shift[mover] = st | 1; compact[mover] = 1; moved = true;
            continue;
          }
        }
        // 3. aşağı (kart bölgesine girmeden); aşağı itilen etiket bir daha yukarı çıkmaz — yoksa yukarı/aşağı arasında salınır
        const down = sy[other]! + LABEL_H_COMPACT + LABEL_GAP;
        if (down <= maxY && sy[mover]! < down) { sy[mover] = down; compact[mover] = 1; _shift[mover] = st | 2; moved = true; }
      }
    }
    if (!moved) break;
  }
}
/** Etiket ayırma (orkestratör kararı 3; yerel sabitler): etiket genişliği ~70 px; dikey çakışma eşiği = itme adımı = gerçek etiket
 *  yüksekliği (ad satırı ~14 + halka 44 + yüzde satırı ~19 ≈ 74 px; CSS .rh-label). Adım bundan küçükse üstteki etiketin adı alttakinin
 *  halkasına/yüzdesine biner. */
export const LABEL_W = 90;   // en uzun adlar ("Kapüşonlu Haydut 4", 12 px) ≈ 105 px → 70 ile adlar yan yana biniyordu
/** Tam etiket yüksekliği: tarayıcıda ölçüldü (getBoundingClientRect 79 px = ad 14 + halka 44 + yüzde 19 + satır payı) — 74 gerçek
 *  yüksekliğin ALTINDA kalıyordu, LABEL_GAP'in 5 px'i buna gidiyordu (3. tur bulgusu). */
export const LABEL_H = 79;
/** Kompakt etiket (itilmiş; halka gizli, "!" yüzdenin yanına): ölçüm 35 px (ad 14 + yüzde 19 + pay) — 38 fazla tahmindi. */
export const LABEL_H_COMPACT = 35;
/** Etiketler arası boşluk (px): itilen etiket alttakinin kabının TAM üstüne oturunca ad, alttaki etiketin yüzdesine bitişik
 *  kalıyordu (3. tur bulgusu — kompakt yükseklik 38, gerçek içerik ~33 → pay sıfırlanıyordu). */
export const LABEL_GAP = 6;
/** Etiket tepesinin inebileceği en küçük y (px): sütun ekranın üstünden taşmasın. */
export const LABEL_TOP_MARGIN = 6;
/** Yatay kaçış adımı (px): LABEL_W'den BÜYÜK olmalı, aksi hâlde çift hâlâ çakışık sayılır ve kaçış işe yaramaz. */
const LABEL_SHIFT_X = LABEL_W + LABEL_GAP;
/** Bağlantı çizgisi (itilen etiket → baş): en az bu kadar kayma varsa çizilir, en fazla bu kadar uzar (px). */
const LEAD_MIN_PX = 12, LEAD_MAX_PX = 220;
/** Alt kart bölgesi (bottom 12 + kart ~56 + pay): etiketler bunun üstüne kelepçelenir; dövüşçü gövdesi bu bölgeye girince kart soluklaşır. */
const CARD_ZONE_PX = 76;
/** Baş noktasından gövde altına kabaca piksel (kart örtüşme sezgisi). */
const BODY_PX = 80;
const CARD_DIM_OPACITY = '0.35';
/** RING OUT bandı bölgesinde etiket saydamlığı (band .rh-center top 18% + ~60 px yükseklik). */
const BANNER_DIM_OPACITY = '0.6';
const BANNER_TOP_FRAC = 0.18;
const BANNER_H_PX = 60;
/** Kart x-aralıkları / etiket düzeni yenileme kadansı (sn) — offsetLeft okuması layout zorlayabilir, karede bir okunmaz. */
const LAYOUT_REFRESH_SEC = 0.5;
/** Etiket bayrak maskesi bitleri (son yazılan sınıf durumu önbelleği). */
const LF_BANG = 1, LF_STUNMAX = 2, LF_DANGER = 4, LF_GUARD = 8, LF_CRACK = 16, LF_MASH = 32, LF_PROT = 64, LF_DIM = 128, LF_COMPACT = 256;
/** Kart bayrakları: bit 0..7 can noktaları (açık = 1), bit 8 elendi, bit 9 soluk. */
const CF_OUT = 1 << 8, CF_DIM = 1 << 9;

export class Hud {
  readonly root: HTMLDivElement;
  private readonly labels: (Label | null)[] = new Array<Label | null>(MAX_FIGHTERS).fill(null);
  private readonly cards: (Card | null)[] = new Array<Card | null>(MAX_FIGHTERS).fill(null);
  /** Kartta YAZILI olan ping (ms; −1 = boş) — yalnız değişince DOM'a dokunulsun. */
  private readonly pingLast: number[] = new Array<number>(MAX_FIGHTERS).fill(-2);
  private pingAcc = PING_REFRESH_SEC;
  private readonly labelsLayer: HTMLDivElement;
  private readonly cardsLayer: HTMLDivElement;
  private readonly countEl: HTMLDivElement;
  private readonly ringoutEl: HTMLDivElement;
  private readonly panelEl: HTMLDivElement;
  private readonly edgeEl: HTMLDivElement;
  private readonly statsEl: HTMLDivElement;
  /** İstatistik kutusu açık mı (çalışma anında `I` ile değişir; `?stats=1` başlangıcı verir). */
  private statsOn = false;
  private ringoutUntil = -1; private countUntil = -1; private edgeUntil = -1;
  private time = 0;
  private w = 1; private h = 1;
  /** Etiket ekran koordinatları + görünürlük maskesi (ayırma geçişi için; tahsissiz). */
  private readonly sx = new Float64Array(MAX_FIGHTERS);
  private readonly sy = new Float64Array(MAX_FIGHTERS);
  private readonly sxRaw = new Float64Array(MAX_FIGHTERS);
  private readonly syRaw = new Float64Array(MAX_FIGHTERS);
  private readonly vis = new Uint8Array(MAX_FIGHTERS);
  private readonly order = new Uint8Array(MAX_FIGHTERS);
  private readonly compact = new Uint8Array(MAX_FIGHTERS);
  /** Son yazılan DOM değerleri (yalnız değişince yaz): −1 = hiç yazılmadı. */
  private readonly lastShown = new Uint8Array(MAX_FIGHTERS);
  private readonly lastX = new Int32Array(MAX_FIGHTERS);
  private readonly lastY = new Int32Array(MAX_FIGHTERS);
  private readonly lastPct = new Int32Array(MAX_FIGHTERS).fill(-1);
  private readonly lastStun = new Int32Array(MAX_FIGHTERS).fill(-1);
  private readonly lastFlags = new Int32Array(MAX_FIGHTERS).fill(-1);
  /** Bağlantı çizgisi son yazılan uzunluk/açı (px, derece). */
  private readonly lastLeadLen = new Int32Array(MAX_FIGHTERS).fill(-1);
  private readonly lastLeadRot = new Int32Array(MAX_FIGHTERS).fill(-1);
  private readonly cardLastPct = new Int32Array(MAX_FIGHTERS).fill(-1);
  private readonly cardLastFlags = new Int32Array(MAX_FIGHTERS).fill(-1);
  /** Kart x-aralıkları (ekran px; LAYOUT_REFRESH_SEC kadansında yenilenir). */
  private readonly cardL = new Float64Array(MAX_FIGHTERS);
  private readonly cardR = new Float64Array(MAX_FIGHTERS);
  private layoutAcc = LAYOUT_REFRESH_SEC;
  private ro: ResizeObserver | null = null;
  /** Dövüşçü kimliği → yerel oyuncu indeksi (0..3; renk ve "P2" etiketi buradan), −1 = yerel değil (bot/uzak). */
  private readonly localIdx = new Int8Array(MAX_FIGHTERS).fill(-1);
  /** Ayırma geçişinde sabit kalan etiketler (bit i = i. dövüşçü yerel oyuncu). */
  private anchorMask = 0;
  private localCount = 0;
  /** Yerel küme değişti → bir sonraki update'te var olan etiket/kartların ad, renk ve mash metni yenilenir. */
  private localDirty = false;

  constructor(readonly deps: HudDeps) {
    const doc = document;
    this.readLocal(deps.localPlayers ?? (deps.playerId !== undefined && deps.playerId >= 0 ? [{ fighterId: deps.playerId, index: 0 }] : []));
    if (!doc.getElementById('ringout-hud-style')) { const st = doc.createElement('style'); st.id = 'ringout-hud-style'; st.textContent = CSS; doc.head.appendChild(st); }
    this.root = doc.createElement('div'); this.root.id = 'rhud';
    this.labelsLayer = doc.createElement('div'); this.root.appendChild(this.labelsLayer);
    this.cardsLayer = doc.createElement('div'); this.cardsLayer.className = 'rh-cards'; this.root.appendChild(this.cardsLayer);
    const center = doc.createElement('div'); center.className = 'rh-center'; this.root.appendChild(center);
    this.countEl = doc.createElement('div'); this.countEl.className = 'rh-count'; center.appendChild(this.countEl);
    this.ringoutEl = doc.createElement('div'); this.ringoutEl.className = 'rh-ringout'; center.appendChild(this.ringoutEl);
    this.panelEl = doc.createElement('div'); this.panelEl.className = 'rh-panel'; center.appendChild(this.panelEl);
    this.edgeEl = doc.createElement('div'); this.edgeEl.className = 'rh-edge'; this.root.appendChild(this.edgeEl);
    this.statsEl = doc.createElement('div'); this.statsEl.className = 'rh-stats'; this.root.appendChild(this.statsEl);
    this.statsOn = deps.stats === true;
    if (this.statsOn) this.statsEl.style.display = 'block';
    (deps.container ?? doc.body).appendChild(this.root);
    // Viewport boyutu önbellekte (update'te clientWidth okuması DOM yazımlarından hemen sonra senkron layout tetikleyebilir):
    // ResizeObserver varsa o, yoksa LAYOUT_REFRESH_SEC kadansındaki ölçüm (refreshCardLayout zaten layout okur) + dışarıdan resize(w,h).
    this.measure();
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver((entries) => {
        const r = entries[0]?.contentRect;
        if (r) this.resize(r.width, r.height); else this.measure();
      });
      this.ro.observe(this.root);
    }
  }

  /** Viewport boyutu (px); Game.onResize çağırabilir — ResizeObserver de aynı yolu besler. */
  resize(w: number, h: number): void { this.w = w || 1; this.h = h || 1; }
  private measure(): void { this.resize(this.root.clientWidth, this.root.clientHeight); }

  /** Yerel oyuncu kümesini okur (kimlik → indeks eşlemesi + sabit maskesi). Aynı dövüşçüyü iki oyuncu işaret edemez; son yazan kazanır. */
  private readLocal(list: readonly LocalPlayerRef[]): void {
    this.localIdx.fill(-1); this.anchorMask = 0; this.localCount = 0;
    for (const p of list) {
      if (p.fighterId < 0 || p.fighterId >= MAX_FIGHTERS) continue;
      if (this.localIdx[p.fighterId] === -1) this.localCount++;   // aynı dövüşçü iki kez gelirse sayaç şişmesin
      this.localIdx[p.fighterId] = p.index; this.anchorMask |= 1 << p.fighterId;
    }
  }
  /** Yerel oyuncu kümesini maç sırasında değiştirir (dövüşçü kimlikleri atandığında / bir oyuncu ayrıldığında).
   *  DOM yenilemesi bir sonraki update'te yapılır: ad metni dünya durumunu (charIdx) ister, burada dünya elde yok. */
  setLocalPlayers(list: readonly LocalPlayerRef[]): void { this.readLocal(list); this.localDirty = true; }
  /** i. dövüşçünün yerel oyuncu indeksi (−1 = değil). */
  private localOf(id: number): number { return this.localIdx[id] ?? -1; }

  /** Ad: yerel oyuncu tek kişiyse sade karakter adı (bugünkü tek oyunculu görünüm). Aynı ekranda birden çok kişi varsa AYNI karakteri
   *  seçebilirler → yerel oyuncular "Şövalye P2" gibi oyuncu etiketiyle, botlar bugünkü gibi kimlik numarasıyla ayrışır. */
  private nameOf(id: number, world: SimWorld): string {
    const f = world.fighters[id];
    const def = f ? this.deps.chars[f.charIdx] : undefined;
    if (!def) return `#${id}`;
    const pi = this.localOf(id);
    if (pi < 0) return `${def.label} ${id}`;
    return this.localCount > 1 ? `${def.label} ${playerColor(pi).label}` : def.label;
  }

  private ensureLabel(f: Fighter, world: SimWorld): Label {
    let l = this.labels[f.id];
    if (l) return l;
    const doc = document;
    const pi = this.localOf(f.id);
    const root = doc.createElement('div'); root.className = 'rh-label';
    if (pi >= 0) root.style.setProperty('--pc', playerColor(pi).css);   // ad çerçevesi, ok ve mash bu değişkeni okur
    const name = doc.createElement('span'); name.className = 'n' + (pi >= 0 ? ' me' : ''); name.textContent = this.nameOf(f.id, world);
    const nWrap = doc.createElement('div'); nWrap.appendChild(name);
    const ring = doc.createElement('div'); ring.className = 'ring';
    const svg = doc.createElementNS('http://www.w3.org/2000/svg', 'svg'); svg.setAttribute('viewBox', '0 0 44 44');
    const mk = (cls: string): SVGCircleElement => { const c = doc.createElementNS('http://www.w3.org/2000/svg', 'circle'); c.setAttribute('cx', '22'); c.setAttribute('cy', '22'); c.setAttribute('r', '20'); c.setAttribute('class', cls); svg.appendChild(c); return c; };
    const guard = mk('guard'); const stun = mk('stun');
    ring.appendChild(svg);
    const bang = doc.createElement('div'); bang.className = 'bang'; bang.textContent = '!'; ring.appendChild(bang);
    const pct = doc.createElement('div'); pct.className = 'p';
    // Mash göstergesi DESIGN §14'te "her dövüşçünün üstünde", ama basış ÇAĞRISI ("BAS!") yalnız YEREL oyunculara anlamlı: bot
    // etiketinde sadece boğuşma ikonu kalır (3. tur bulgusu: sahne kalabalıklaşıyor, oyuncu botun ipucunu kendine sanıyor).
    const mash = doc.createElement('div'); mash.className = 'mash'; mash.textContent = pi >= 0 ? '⟲ BAS!' : '⟲';
    const lead = doc.createElement('div'); lead.className = 'lead';   // itilen etiketi başına bağlayan çizgi (uzunluk/açı her karede)
    root.append(nWrap, ring, pct, mash, lead);
    this.labelsLayer.appendChild(root);
    l = { root, name, pct, stun, guard, bang, mash, lead };
    this.labels[f.id] = l;
    return l;
  }
  private ensureCard(f: Fighter, world: SimWorld): Card {
    let c = this.cards[f.id];
    if (c) return c;
    const doc = document;
    const def = this.deps.chars[f.charIdx];
    const pi = this.localOf(f.id);
    const root = doc.createElement('div'); root.className = 'rh-card' + (pi >= 0 ? ' me' : '');
    if (pi >= 0) root.style.setProperty('--pc', playerColor(pi).css);
    const pic = doc.createElement('div'); pic.className = 'pic';
    const url = def ? PORTRAIT_URLS[def.id] : null;
    if (url) pic.style.backgroundImage = `url("${assetUrl(url)}")`; else pic.textContent = (def?.label ?? '?').slice(0, 1);
    const col = doc.createElement('div');
    const nm = doc.createElement('div'); nm.className = 'nm';
    const who = doc.createElement('span'); who.className = 'who'; who.textContent = this.nameOf(f.id, world);
    const ping = doc.createElement('span'); ping.className = 'ping';
    nm.append(who, ping);
    const pct = doc.createElement('div'); pct.className = 'pct';
    const lives = doc.createElement('div'); lives.className = 'lives';
    const dots: HTMLElement[] = [];
    for (let i = 0; i < T.RESPAWN.lives; i++) { const d = doc.createElement('i'); lives.appendChild(d); dots.push(d); }
    col.append(nm, pct, lives); root.append(pic, col);
    this.cardsLayer.appendChild(root);
    c = { root, nm: who, ping, pct, lives: dots };
    this.cards[f.id] = c;
    this.layoutAcc = LAYOUT_REFRESH_SEC;   // yeni kart → x-aralıkları bu karede yenilensin
    return c;
  }

  /* ---------------------------------------------------------------- olaylar */

  onEvent(e: SimEvent, world: SimWorld): void {
    switch (e.type) {
      case Ev.Countdown: this.countEl.textContent = String(e.v0); this.countEl.style.display = 'block'; this.countUntil = this.time + 0.9; break;
      case Ev.MatchStart: this.countEl.textContent = 'DÖVÜŞ!'; this.countEl.style.display = 'block'; this.countUntil = this.time + 0.8; break;
      case Ev.Fell:
        this.ringoutEl.textContent = attributionText(e, (id) => this.nameOf(id, world));
        this.ringoutEl.style.display = 'block'; this.ringoutUntil = this.time + HUD.ringoutSec;
        if (HUD.edgeFlashSec > 0) {   // 0 = flaş kapalı (kullanıcı isteği); DOM'a hiç dokunma
          this.edgeEl.className = 'rh-edge ' + edgeSide(e.dx, e.dz); this.edgeEl.style.opacity = '1'; this.edgeUntil = this.time + HUD.edgeFlashSec;
        }
        break;
      case Ev.MatchEnd: this.showWinner(e.v0, world); break;
      default: break;
    }
  }

  private showWinner(winner: number, world: SimWorld): void {
    const p = this.panelEl;
    p.replaceChildren();
    const h2 = document.createElement('h2'); h2.textContent = winner >= 0 ? `KAZANAN: ${this.nameOf(winner, world)}` : 'BERABERE'; p.appendChild(h2);
    const table = document.createElement('table');
    const head = table.insertRow(); for (const t of ['Dövüşçü', 'Ring-out', 'Düşme', 'Yediği en uzun kombo']) { const th = document.createElement('th'); th.textContent = t; head.appendChild(th); }
    const sorted = [...world.fighters].sort((a, b) => b.kos - a.kos || a.falls - b.falls);
    for (const f of sorted) { const r = table.insertRow(); for (const v of [this.nameOf(f.id, world), String(f.kos), String(f.falls), String(f.longestComboOnMe)]) r.insertCell().textContent = v; }
    p.appendChild(table);
    const btns = document.createElement('div'); btns.className = 'btns';
    const menu = document.createElement('button');
    if (this.deps.winnerActions === 'net') {
      // Ağ maçında "Tekrar" diye bir şey YOK: yeni maçı oda başlatır, tek yol lobiye dönmek. İki düğme
      // göstermek, biri hiç çalışmayacakken, yanlış vaat olurdu.
      menu.textContent = 'Lobiye dön'; menu.onclick = () => this.deps.onMenu?.();
      btns.append(menu);
    } else {
      const again = document.createElement('button'); again.textContent = 'Tekrar'; again.onclick = () => this.deps.onRematch?.();
      menu.textContent = 'Menü'; menu.onclick = () => this.deps.onMenu?.();
      btns.append(again, menu);
    }
    p.appendChild(btns);
    p.style.display = 'block';
  }
  hideWinner(): void { this.panelEl.style.display = 'none'; }

  /* ---------------------------------------------------------------- kare */

  /**
   * İstatistik kutusunu çalışma anında aç/kapat (oyunda `I` tuşu). `?stats=1` başlangıç değerini verir;
   * bu bayrak onun üstüne biner, böylece oyuncu maç ortasında fps'e bakıp kapatabilir.
   */
  setStatsVisible(on: boolean): void {
    this.statsOn = on;
    this.statsEl.style.display = on ? 'block' : 'none';
    if (!on) this.statsEl.textContent = '';
  }
  get statsVisible(): boolean { return this.statsOn; }

  setStats(s: HudStats): void {
    if (!this.statsOn) return;
    const draw = s.drawCallsShadow !== undefined && s.drawCallsNoShadow !== undefined
      ? `draw ${s.drawCalls} (gölge ${s.drawCallsShadow} / sahne ${s.drawCallsNoShadow})` : `draw ${s.drawCalls}`;
    const bot = s.msBot !== undefined ? `  bot ${s.msBot.toFixed(2)} ms` : '';
    const present = s.msPresent !== undefined ? `  present ${s.msPresent.toFixed(2)} ms` : '';
    const dropped = s.eventsDropped !== undefined ? `  olay düşen ${s.eventsDropped}` : '';
    const slow = s.timeScale !== undefined && s.timeScale < 1 ? `  ⏱ AĞIR ÇEKİM ×${s.timeScale.toFixed(2)}` : '';
    this.statsEl.textContent = `fps ${s.fps}  sim ${s.msSim.toFixed(2)} ms${bot}${present}  view ${s.msView.toFixed(2)} ms  render ${s.msRender.toFixed(2)} ms\n${draw}  tier ${s.tier}${dropped}${slow}`;
  }

  /**
   * Kart adlarının yanına ping yazar. Ölçüm sunucudan saniyede bir geliyor; DOM'a her karede dokunmanın
   * anlamı yok (metin değişmese bile `textContent` ataması düzen hesabı tetikleyebilir) — bu yüzden
   * hem seyrek çağrılıyor hem de yalnız DEĞİŞEN değer yazılıyor.
   */
  private refreshPings(world: SimWorld): void {
    const pingOf = this.deps.pingOf;
    if (!pingOf) return;
    for (const f of world.fighters) {
      const c = this.cards[f.id];
      if (!c) continue;
      const ms = pingOf(f.id);
      // Bot ya da henüz ölçülmemiş: rozet BOŞ kalır (`:empty` gizliyor) — "0 ms" yazmak yanlış bilgi olurdu.
      const v = ms < 0 ? -1 : Math.min(999, Math.round(ms));
      if (v === this.pingLast[f.id]) continue;
      this.pingLast[f.id] = v;
      c.ping.textContent = v < 0 ? '' : `${v} ms`;
      c.ping.classList.toggle('mid', v >= PING_MID_MS && v < PING_BAD_MS);
      c.ping.classList.toggle('bad', v >= PING_BAD_MS);
    }
  }

  /** Kart x-aralıklarını okur (offsetLeft/offsetWidth; kartlar statik → seyrek). */
  private refreshCardLayout(): void {
    for (let i = 0; i < MAX_FIGHTERS; i++) {
      const c = this.cards[i];
      if (!c) { this.cardL[i] = 0; this.cardR[i] = -1; continue; }
      const l = c.root.offsetLeft;
      this.cardL[i] = l; this.cardR[i] = l + c.root.offsetWidth;
    }
  }

  /** Yerel küme değişince var olan DOM'u yeniler (ad, "me" işaretleri, oyuncu rengi, mash çağrısı). Seyrek: yalnız setLocalPlayers sonrası. */
  private refreshLocalMarks(world: SimWorld): void {
    for (let id = 0; id < MAX_FIGHTERS; id++) {
      const pi = this.localOf(id);
      const l = this.labels[id];
      if (l) {
        l.name.className = 'n' + (pi >= 0 ? ' me' : '');
        l.name.textContent = this.nameOf(id, world);
        l.mash.textContent = pi >= 0 ? '⟲ BAS!' : '⟲';
        if (pi >= 0) l.root.style.setProperty('--pc', playerColor(pi).css); else l.root.style.removeProperty('--pc');
      }
      const c = this.cards[id];
      if (c) {
        c.root.classList.toggle('me', pi >= 0);
        c.nm.textContent = this.nameOf(id, world);
        if (pi >= 0) c.root.style.setProperty('--pc', playerColor(pi).css); else c.root.style.removeProperty('--pc');
      }
    }
  }

  update(world: SimWorld, dt: number): void {
    this.time += dt;
    this.pingAcc += dt;
    if (this.pingAcc >= PING_REFRESH_SEC && this.deps.pingOf) { this.pingAcc = 0; this.refreshPings(world); }
    if (this.localDirty) { this.localDirty = false; this.refreshLocalMarks(world); }
    const cam = this.deps.camera;
    this.layoutAcc += dt;
    if (this.layoutAcc >= LAYOUT_REFRESH_SEC) { this.layoutAcc = 0; this.refreshCardLayout(); if (!this.ro) this.measure(); }
    const w = this.w, h = this.h;
    const bannerOn = this.ringoutUntil >= 0;
    const bannerTop = h * BANNER_TOP_FRAC, bannerBottom = bannerTop + BANNER_H_PX;
    const cardTop = h - CARD_ZONE_PX;

    // 1. geçiş: kartlar + etiket ekran koordinatları (DOM'a henüz yazılmaz)
    this.vis.fill(0);
    let dimMask = 0;   // bit i: i. kart soluk
    for (const f of world.fighters) {
      const id = f.id;
      const l = this.ensureLabel(f, world);
      this.ensureCard(f, world);
      const gone = f.state === FState.Eliminated;
      // Ring-out düşüşünde (Falling: kamera dışına çıkar, 0.25 s'de küçülür) etiket gizli — kart bölgesine kelepçelenip köşede asılı kalmasın
      const visible = !gone && f.state !== FState.Respawning && f.state !== FState.Falling && this.deps.headWorld(id, _v);
      if (!visible) { this.hideLabel(l, id); continue; }
      _w.set(_v.x, _v.y, _v.z).project(cam);
      if (_w.z > 1 || _w.z < -1) { this.hideLabel(l, id); continue; }
      const x = (_w.x + 1) / 2 * w, y = (1 - _w.y) / 2 * h;
      if (x < -LABEL_W || x > w + LABEL_W || y < -LABEL_H || y > h + LABEL_H) { this.hideLabel(l, id); continue; }   // viewport dışı
      this.vis[id] = 1; this.sx[id] = x; this.sxRaw[id] = x; this.syRaw[id] = y;
      this.sy[id] = Math.min(y, cardTop);   // etiket kart bölgesinin üstünde kalır
      // Gövde kart bölgesine giriyorsa hangi kart(lar)ın üstünde → soluklaştır
      if (y + BODY_PX > cardTop) {
        for (let i = 0; i < MAX_FIGHTERS; i++) if (this.cardR[i]! >= 0 && x >= this.cardL[i]! && x <= this.cardR[i]!) dimMask |= 1 << i;
      }
    }
    separateLabels(this.sx, this.sy, this.vis, this.order, this.compact, this.anchorMask, w, cardTop);

    // 2. geçiş: DOM yazımları (yalnız değişince)
    for (const f of world.fighters) {
      const id = f.id;
      const c = this.cards[id]!;
      const gone = f.state === FState.Eliminated;
      // Köşe kartı
      const pct = Math.round(f.damage);
      if (pct !== this.cardLastPct[id]) { this.cardLastPct[id] = pct; c.pct.textContent = `${pct}%`; c.pct.style.color = damageColor(f.damage); }
      let cf = 0;
      for (let i = 0; i < c.lives.length; i++) if (i < f.lives) cf |= 1 << i;
      if (gone) cf |= CF_OUT;
      if (dimMask & (1 << id)) cf |= CF_DIM;
      const cPrev = this.cardLastFlags[id]!;
      if (cf !== cPrev) {
        this.cardLastFlags[id] = cf;
        for (let i = 0; i < c.lives.length; i++) { const on = (cf >> i) & 1, was = (cPrev >> i) & 1; if (cPrev < 0 || on !== was) c.lives[i]!.classList.toggle('off', !on); }
        if (cPrev < 0 || (cf & CF_OUT) !== (cPrev & CF_OUT)) c.root.classList.toggle('out', gone);
        if (cPrev < 0 || (cf & CF_DIM) !== (cPrev & CF_DIM)) c.root.style.opacity = cf & CF_DIM ? CARD_DIM_OPACITY : '';
      }
      // Baş üstü etiket
      if (!this.vis[id]) continue;
      const l = this.labels[id]!;
      const x = Math.round(this.sx[id]!), y = Math.round(this.sy[id]!);
      if (!this.lastShown[id]) { this.lastShown[id] = 1; l.root.style.display = 'block'; }
      if (x !== this.lastX[id] || y !== this.lastY[id]) {
        this.lastX[id] = x; this.lastY[id] = y;
        l.root.style.transform = `translate(${x}px, ${y}px) translate(-50%,-100%)`;
      }
      // Bağlantı çizgisi (yalnız itilmiş = kompakt etiket): etiketin altından gerçek baş noktasına doğru döner (hangi etiket kime ait
      // sorusu kalabalıkta okunmuyordu — 3. tur bulgusu). Uzunluk/açı değişmezse DOM'a yazılmaz.
      const ldx = this.sxRaw[id]! - this.sx[id]!, ldy = this.syRaw[id]! - this.sy[id]!;
      const ld = Math.sqrt(ldx * ldx + ldy * ldy);
      const lead = this.compact[id] && ld > LEAD_MIN_PX ? Math.min(Math.round(ld), LEAD_MAX_PX) : 0;
      const rot = lead ? Math.round(Math.atan2(-ldx, ldy) * 180 / Math.PI) : 0;
      if (lead !== this.lastLeadLen[id] || rot !== this.lastLeadRot[id]) {
        this.lastLeadLen[id] = lead; this.lastLeadRot[id] = rot;
        l.lead.style.height = `${lead}px`;
        l.lead.style.transform = `translateX(-50%) rotate(${rot}deg)`;
      }
      if (pct !== this.lastPct[id]) { this.lastPct[id] = pct; l.pct.textContent = `${pct}%`; l.pct.style.color = damageColor(f.damage); }
      const stun100 = Math.round(stunFraction(f.stun) * 100);
      if (stun100 !== this.lastStun[id]) { this.lastStun[id] = stun100; l.stun.style.strokeDashoffset = String(STUN_CIRC * (1 - stun100 / 100)); }
      let lf = 0;
      if (stunFinishable(f.stun)) lf |= LF_BANG;
      if (f.stun >= T.COMBAT.stunMax) lf |= LF_STUNMAX;
      if (f.damage >= HUD.dangerPct) lf |= LF_DANGER;
      if (f.blocking) { lf |= LF_GUARD; if (guardCracked(f.guard)) lf |= LF_CRACK; }
      if (f.state === FState.Held || f.state === FState.Carried) lf |= LF_MASH;
      if (f.spawnProtUntil > world.tick) lf |= LF_PROT;
      if (bannerOn && y >= bannerTop - LABEL_H && y - BODY_PX <= bannerBottom) lf |= LF_DIM;
      if (this.compact[id]) lf |= LF_COMPACT;
      const prev = this.lastFlags[id]!;
      if (lf !== prev) {
        this.lastFlags[id] = lf;
        const ch = prev < 0 ? -1 : lf ^ prev;   // değişen bitler (ilk yazımda hepsi)
        if (ch & LF_BANG) { l.bang.classList.toggle('on', (lf & LF_BANG) !== 0); l.pct.classList.toggle('b', (lf & LF_BANG) !== 0); }
        if (ch & LF_COMPACT) l.root.classList.toggle('compact', (lf & LF_COMPACT) !== 0);
        if (ch & LF_STUNMAX) l.root.classList.toggle('stunmax', (lf & LF_STUNMAX) !== 0);
        if (ch & LF_DANGER) l.root.classList.toggle('danger', (lf & LF_DANGER) !== 0);
        if (ch & LF_GUARD) l.guard.classList.toggle('on', (lf & LF_GUARD) !== 0);
        if (ch & LF_CRACK) l.guard.classList.toggle('crack', (lf & LF_CRACK) !== 0);
        if (ch & LF_MASH) l.mash.classList.toggle('on', (lf & LF_MASH) !== 0);
        if (ch & LF_PROT) l.root.classList.toggle('prot', (lf & LF_PROT) !== 0);
        if (ch & LF_DIM) l.root.style.opacity = lf & LF_DIM ? BANNER_DIM_OPACITY : '';
      }
    }
    if (this.countUntil >= 0 && this.time > this.countUntil) { this.countEl.style.display = 'none'; this.countUntil = -1; }
    if (this.ringoutUntil >= 0 && this.time > this.ringoutUntil) { this.ringoutEl.style.display = 'none'; this.ringoutUntil = -1; }
    if (this.edgeUntil >= 0 && this.time > this.edgeUntil) { this.edgeEl.style.opacity = '0'; this.edgeUntil = -1; }
  }

  private hideLabel(l: Label, id: number): void {
    if (this.lastShown[id]) { this.lastShown[id] = 0; l.root.style.display = 'none'; }
  }

  dispose(): void { this.ro?.disconnect(); this.ro = null; this.root.remove(); for (let i = 0; i < MAX_FIGHTERS; i++) { this.labels[i] = null; this.cards[i] = null; } }
}
